import React, { useMemo, useState } from 'react';
import { Box, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { VertragsartenStand } from '../../api/vertragsartenMitLogo';
import Platzhalterleiste, { LEISTE_BREITE_PX, Leistenposition } from './Platzhalterleiste';
import VertragDruckportal from './VertragDruckportal';
import VertragSeiten from './VertragSeiten';
import Werkzeugleiste from './Werkzeugleiste';
import VertragEditor from '../editor/VertragEditor';
import { VertragEditorApi } from '../editor/useVertragEditor';
import { VertragsartenProvider } from './VertragsartenKontext';
import { leistenBedarfPx } from '../layout/leistenplatz';
import { seitengeometrie } from '../layout/seitengeometrie';
import useLeistenposition from '../layout/useLeistenposition';
import useSeitenaufteilung from '../layout/useSeitenaufteilung';
import { Anzeigemodus, Platzhalterbelegung, WERTEMODI, Wertemodus } from '../model/platzhalterwerte';
import {
  Beispielauswahl,
  Beispielfall,
  BEISPIELFAELLE,
  beispielwerte,
  STANDARD_BEISPIELFALL,
  vorlagenwahlSatz,
} from '../model/beispielfaelle';
import '../assets/vertragseditor.css';
import { useVertragLeisten } from './VertragLeistenRahmen';

/** Ohne Angabe des Aufrufers: Grundfall „Berater“ und die Fallback-Marke (feste Identität für die Memoisierung). */
const STANDARD_BEISPIEL: Beispielauswahl = { fall: STANDARD_BEISPIELFALL, marke: null };

export interface VertragArbeitsbereichProps {
  editor: VertragEditorApi;
  vertragsarten: VertragsartenStand;
  gesperrt?: boolean;
  onVertragsartenErneutLaden?: () => void;
  /**
   * RITA-1547 (Entwurf beim Berater): feste Werte statt der Umschaltung „Platzhalterwerte“ und keine
   * Platzhalterleiste – im Entwurf sind die Berater-Daten schon Text, nur das Datum bleibt ein Platzhalter.
   */
  festeWerte?: Wertemodus;
  /**
   * RITA-1597: Beispielfall und Marke der Ansicht „Beispielwerte“ (und der PDF-Probe des Aufrufers); der Fall
   * wird nicht gespeichert und liegt beim Aufrufer. Ohne Angabe gilt der Grundfall „Berater“ mit Fallback-Marke.
   */
  beispiel?: Beispielauswahl;
  onBeispielfallChange?: (fall: Beispielfall) => void;
}

function VertragArbeitsbereich({
  editor,
  vertragsarten,
  gesperrt = false,
  onVertragsartenErneutLaden,
  festeWerte,
  beispiel = STANDARD_BEISPIEL,
  onBeispielfallChange,
}: VertragArbeitsbereichProps) {
  const leisten = useVertragLeisten();
  const [gewaehlterModus, setModus] = useState<Anzeigemodus>('beispiel');
  // Die Beispielwerte des gewählten Falls laufen als Belegung durch Anzeige und Seitenmessung; die Identität
  // bleibt je Fall und Marke stabil, sonst misst `useSeitenaufteilung` bei jedem Rendern neu.
  const beispielbelegung = useMemo<Platzhalterbelegung>(
    () => ({ werte: beispielwerte(beispiel.fall, beispiel.marke) }),
    [beispiel.fall, beispiel.marke],
  );
  const modus: Wertemodus = festeWerte ?? (gewaehlterModus === 'beispiel' ? beispielbelegung : gewaehlterModus);
  const mitPlatzhaltern = festeWerte === undefined;
  // Die Auswahl gibt es nur, wenn der Aufrufer den Fall auch entgegennimmt; sonst wäre sie ein totes Feld.
  const mitBeispielfall = mitPlatzhaltern && gewaehlterModus === 'beispiel' && onBeispielfallChange !== undefined;
  const { dokument } = editor.zustand;
  const geometrie = useMemo(() => seitengeometrie(dokument.seite), [dokument.seite]);
  // RITA-1533 (V8): gewählte Lage aus dem Speicher, wirksame Lage aus der Breite des Flex-Rahmens.
  const bedarf = useMemo(() => leistenBedarfPx(geometrie.breitePx, LEISTE_BREITE_PX), [geometrie.breitePx]);
  const { rahmenRef, gewaehlt, wirksam: leistenposition, waehlen } = useLeistenposition(bedarf);
  const positionWaehlen = (position: Leistenposition) => {
    if (gesperrt) return;
    waehlen(position);
  };
  const { seiten, messung } = useSeitenaufteilung(dokument, modus, geometrie);

  return (
    <VertragsartenProvider value={vertragsarten}>
      {/*
          Die Höhe dieser Leiste ist knapp bemessen: Mit „Leiste oben“ muss am Seitenanfang bei 1280 × 720 noch die
          kompakte Platzhalterleiste unter die Werkzeugleiste passen (RITA-1533/1562, Entscheidung Julia 07.10.2026);
          eine zusätzliche Zeile hier (schon 10 px) lässt sie dort als Ganzes scrollen. Deshalb steht der Satz zur
          Vorlagenwahl (RITA-1597) in der Kopfzeile neben „Platzhalterwerte“, nicht in einer eigenen Zeile.
          RITA-1599 (Entscheidung Julia 03.11.2026): Der Kasten bleibt beim Scrollen unter der Titelzeile und über der
          Werkzeugleiste stehen (nur im Vorlagen-Editor; ab etwa 1024 × 700 px Fenstergröße, Referenz 1280 × 720, siehe `useFesteVertragsleisten`). Der Rahmen
          trägt die Messung und den Abstand nach unten; geklebt schließt die Werkzeugleiste ohne Spalt an.
        */}
      <Box
        ref={mitPlatzhaltern ? leisten?.wertekastenRef : undefined}
        data-testid="vertrag-feste-werte"
        data-fest={mitPlatzhaltern && leisten?.wertekastenFest ? 'ja' : 'nein'}
        sx={{
          position: mitPlatzhaltern && leisten?.wertekastenFest ? 'sticky' : 'static',
          top: leisten?.wertekastenOben,
          zIndex: 10,
          bgcolor: 'background.default',
          mb: 2,
          maxHeight: mitPlatzhaltern && leisten?.wertekastenFest ? leisten.maxLeistenHoehe : undefined,
          overflowY: mitPlatzhaltern && leisten?.wertekastenFest ? 'auto' : undefined,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          {mitPlatzhaltern && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                Platzhalterwerte
              </Typography>
              {mitBeispielfall && (
                <Typography
                  id="vertrag-vorlagenwahl"
                  variant="caption"
                  color="text.secondary"
                  data-testid="vertrag-vorlagenwahl"
                  aria-live="polite"
                  sx={{ ml: 'auto', textAlign: 'right' }}
                >
                  {vorlagenwahlSatz(beispiel.fall)}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {mitPlatzhaltern && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={gewaehlterModus}
                disabled={gesperrt}
                aria-label="Platzhalterwerte"
                onChange={(_event, wert: Anzeigemodus | null) => {
                  if (!gesperrt && wert) setModus(wert);
                }}
              >
                {WERTEMODI.map((eintrag) => (
                  <ToggleButton key={eintrag.wert} value={eintrag.wert}>
                    {eintrag.bezeichnung}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {/* RITA-1597: Beispielfall rechts neben den Knöpfen, nur bei „Beispielwerte“; „Seiten“ bleibt rechts außen. */}
            {mitBeispielfall && (
              <TextField
                select
                size="small"
                label="Beispielfall"
                value={beispiel.fall}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Beispielfall', 'aria-describedby': 'vertrag-vorlagenwahl' }}
                disabled={gesperrt}
                onChange={(event) => {
                  if (!gesperrt) onBeispielfallChange?.(event.target.value as Beispielfall);
                }}
              >
                {BEISPIELFAELLE.map((eintrag) => (
                  <option key={eintrag.wert} value={eintrag.wert}>
                    {eintrag.bezeichnung}
                  </option>
                ))}
              </TextField>
            )}
            <Typography variant="body2" sx={{ ml: 'auto' }} data-testid="vertrag-seitenanzahl">
              {seiten.length > 0 ? `Seiten: ${seiten.length}` : 'Seiten werden berechnet …'}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Box
        ref={leisten?.werkzeugeRef}
        data-testid="vertrag-feste-werkzeuge"
        sx={{
          position: 'sticky',
          top: leisten?.werkzeugeOben,
          zIndex: 10,
          bgcolor: 'background.default',
          // Mit Leiste oben schließt diese ohne Abstand an (auch im Fluss), seitlich bleibt der gewohnte Abstand.
          mb: leistenposition === 'oben' && mitPlatzhaltern ? 0 : 2,
          maxHeight: leisten?.maxUntereLeistenHoehe,
          overflowY: 'auto',
        }}
      >
        <Werkzeugleiste
          editor={editor}
          seiten={seiten}
          gesperrt={gesperrt}
          onVertragsartenErneutLaden={onVertragsartenErneutLaden}
          geltung={mitPlatzhaltern ? 'vorlage' : 'dokument'}
        />
      </Box>

      {/*
          Ein fester Rahmen für alle drei Positionen: Der Seitenbereich steht im DOM immer vor der Leiste
          (Tab führt von der Werkzeugleiste direkt in den Text), die Position steuert nur die CSS-Reihenfolge.
          So wird der Editor-Host beim Umschalten nie neu eingehängt (HOTSPOTS: Host nicht neu mounten).
          Bei „oben“ (Spalte) bekommt der Seitenrahmen seine Inhaltshöhe: `flex: 1 1 0` würde in der Spalte
          für die Höhe gelten und den Rahmen wegen `overflowX: auto` auf seine Innenabstände (48 px)
          schrumpfen lassen (Codex-Prüfung Teilpaket 3). Überall gilt die WIRKSAME Lage (V8): Ist der
          Rahmen zu schmal für Blatt und Leiste, steht die Leiste oben; der Rahmen wird dafür gemessen.
        */}
      <Box
        ref={rahmenRef}
        data-testid="vertrag-leistenrahmen"
        sx={{
          display: 'flex',
          flexDirection: leistenposition === 'oben' ? 'column' : 'row',
          alignItems: leistenposition === 'oben' ? 'stretch' : 'flex-start',
          gap: 2,
        }}
      >
        <Box
          data-testid="vertrag-seitenrahmen"
          sx={{
            bgcolor: '#cfd6df',
            p: 3,
            borderRadius: 1,
            overflowX: 'auto',
            flex: leistenposition === 'oben' ? '0 0 auto' : '1 1 0',
            minWidth: 0,
            order: 0,
          }}
        >
          {messung}
          <VertragEditor
            editor={editor}
            seiten={seiten}
            modus={modus}
            geometrie={geometrie}
            gesperrt={gesperrt}
            scrollRandOben={leisten?.scrollRandOben}
          />
        </Box>
        {mitPlatzhaltern && (
          <Platzhalterleiste
            editor={editor}
            gesperrt={gesperrt}
            position={leistenposition}
            gewaehlt={gewaehlt}
            bedarfPx={bedarf}
            onPositionChange={positionWaehlen}
            reihenfolge={leistenposition === 'rechts' ? 1 : -1}
            abstandOben={leisten?.platzhalterOben}
            fensterHoehe={leisten?.fensterHoehe}
            onKlebeElement={leisten?.obereLeisteMelden}
            maxHoehe={leisten?.maxUntereLeistenHoehe}
          />
        )}
      </Box>

      <VertragDruckportal>
        <VertragSeiten dokument={dokument} seiten={seiten} modus={modus} geometrie={geometrie} druck />
      </VertragDruckportal>
    </VertragsartenProvider>
  );
}

export default VertragArbeitsbereich;
